import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HistoryIcon from '@mui/icons-material/History';
import { useAppState } from '../context/LoadTestContext';
import { fetchCalibrationRevisions, fetchLoadTestRecord, fetchPastTestData } from '../services/api';
import CalibrationHost from '../load_test_components/CalibrationHost';
import DashboardBody from '../load_test_components/v2/DashboardBody';
import { useCalibrationCommit } from '../load_test_components/useCalibrationCommit';
import { formatSavedAt, historyRevisionPath, historyTestPath, testSummary, testTitle } from '../load_test_components/testRecord';

// Operations > Load Testing > Historical Data > a test > a revision: the test's
// dashboard from its own readings, converted with that revision. Same layout
// as Live Tests, with a banner in place of the box picker that says which
// revision is shown and which one the test uses, and switches between them.
// Viewing a revision never changes the test; "Use Rev N for this test" does.

function RevisionBanner({ test, testId, revisionId }) {
  const navigate = useNavigate();
  const { viewing, loadTest } = useAppState();
  const { apply } = useCalibrationCommit();
  const [revisions, setRevisions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // The revision list, for the dropdown; reloaded when the one in use changes
  // (a save adds one).
  useEffect(() => {
    let current = true;
    fetchCalibrationRevisions(testId)
      .then((data) => { if (current) setRevisions(data.revisions || []); })
      .catch(() => { if (current) setRevisions([]); });
    return () => { current = false; };
  }, [testId, viewing?.active_revision_id]);

  const useThis = async () => {
    setBusy(true);
    setError(null);
    try {
      await apply(revisionId);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const shown = viewing && Number(viewing.id) === Number(revisionId) ? viewing : null;

  return (
    <Paper sx={{ px: 1.5, py: 1, borderRadius: 1, display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 2, rowGap: 1 }}>
      <Button size='small' startIcon={<ArrowBackIcon />} onClick={() => navigate(historyTestPath(testId))} sx={{ textTransform: 'none' }}>
        Back to test
      </Button>
      <Box sx={{ minWidth: 0, flex: '1 1 260px' }}>
        <Typography variant='subtitle1' fontWeight={600} noWrap title={testTitle(test)}>{test ? testTitle(test) : 'Past test'}</Typography>
        <Typography variant='caption' color='text.secondary' component='div'>{testSummary(test)}</Typography>
      </Box>
      <Stack direction='row' alignItems='center' flexWrap='wrap' useFlexGap spacing={1}>
        <HistoryIcon fontSize='small' color='action' />
        <TextField
          select
          size='small'
          label='Revision'
          value={revisions.some((r) => r.id === Number(revisionId)) ? Number(revisionId) : ''}
          onChange={(e) => navigate(historyRevisionPath(testId, e.target.value))}
          SelectProps={{
            renderValue: (id) => {
              const r = revisions.find((x) => x.id === id);
              return r ? `Rev ${r.rev_number}${r.is_active ? ' · in use' : ''}` : '';
            },
          }}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 160 }}
        >
          {revisions.map((r) => (
            <MenuItem key={r.id} value={r.id}>
              <Box>
                <Typography variant='body2' component='div'>Rev {r.rev_number}{r.is_active ? ' · in use' : ''}</Typography>
                <Typography variant='caption' color='text.secondary' component='div'>
                  {[r.created_by, formatSavedAt(r.created_at)].filter(Boolean).join(', ')}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </TextField>
        {shown && (shown.in_use
          ? <Chip size='small' color='success' variant='outlined' label='In use' />
          : <Chip size='small' color='warning' variant='outlined' label={`The test uses Rev ${shown.active_rev_number}`} />)}
      </Stack>
      {shown && (
        <Typography variant='caption' color='text.secondary' sx={{ flex: '1 1 200px', minWidth: 0 }}>
          {[shown.created_by && `Saved by ${shown.created_by}`, formatSavedAt(shown.created_at), shown.note].filter(Boolean).join(' · ')}
        </Typography>
      )}
      {shown && !shown.in_use && loadTest?.id === Number(testId) && (
        <Button size='small' variant='outlined' onClick={useThis} disabled={busy} sx={{ textTransform: 'none', ml: 'auto' }}>
          {busy ? 'Switching…' : `Use Rev ${shown.rev_number} for this test`}
        </Button>
      )}
      {error && <Alert severity='error' sx={{ py: 0, flexBasis: '100%' }}>{error}</Alert>}
    </Paper>
  );
}

function LoadTestPastTestDashboard() {
  const params = useParams();
  const testId = Number(params.testId);
  const revisionId = Number(params.revisionId);
  const navigate = useNavigate();
  const location = useLocation();
  const {
    setLoading, applyPolledData, setHistoryView, viewing, loadTest, calPreview, setCalDialogOpen,
  } = useAppState();
  const [test, setTest] = useState(null);
  const [error, setError] = useState(null);

  // Calibration requests made here answer for this test and revision.
  useEffect(() => {
    setHistoryView({ testId, revisionId });
    return () => setHistoryView(null);
  }, [testId, revisionId, setHistoryView]);

  useEffect(() => {
    let current = true;
    fetchLoadTestRecord(testId)
      .then((data) => { if (current) setTest(data.test); })
      .catch(() => { if (current) setTest(null); });
    return () => { current = false; };
  }, [testId, viewing?.active_revision_id]);

  useEffect(() => {
    let current = true;
    setLoading(true);
    setError(null);
    fetchPastTestData(testId, revisionId)
      .then((data) => { if (current) applyPolledData(data); })
      .catch((err) => { if (current) setError(err.message); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [testId, revisionId]);

  // Saving a revision, or putting another in use from the Calibration window,
  // shows the test with the revision now in use: follow it in the address. Only
  // when the data changed under the revision in the address, not while the
  // address itself is moving to another revision.
  const lastViewing = useRef(null);
  useEffect(() => {
    const before = lastViewing.current;
    lastViewing.current = viewing;
    if (!viewing || !before || calPreview) return;
    if (Number(before.id) === revisionId && Number(viewing.id) !== revisionId) {
      navigate(historyRevisionPath(testId, viewing.id), { replace: true });
    }
  }, [viewing]);

  // "New revision" on the test's page opens the Calibration window here.
  useEffect(() => {
    if (!location.state?.calibrate || loadTest?.id !== testId) return;
    setCalDialogOpen(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, loadTest?.id]);

  return (
    <>
      <DashboardBody
        banner={<RevisionBanner test={test} testId={testId} revisionId={revisionId} />}
        errorMessage={error}
        resetKey={`${testId}-${revisionId}`}
      />
      <CalibrationHost />
    </>
  );
}

export default LoadTestPastTestDashboard;
